"use client";

import { useMemo, useState } from "react";
import {
  Area,
  Bar,
  Cell,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  ReferenceLine,
  ComposedChart,
} from "recharts";
import { FONT_BASTION, FONT_MONO } from "@/lib/bastion-button";

const DATA_CENTER_COLOR = "#F2C94C";
const GENERAL_OFFICE_COLOR = "#B794F4";
const POSITIVE_COLOR = "#3bd576";
const NEGATIVE_COLOR = "#ef4444";

export type ChartPoint = {
  date: string;            // YYYY-MM-01
  dataCenter: number | null;
  general: number | null;
  total: number | null;
};

type RangeKey = "all" | "10y" | "5y" | "3y";

const RANGES: { key: RangeKey; label: string; months: number | null }[] = [
  { key: "all", label: "All", months: null },
  { key: "10y", label: "10Y", months: 120 },
  { key: "5y",  label: "5Y",  months: 60  },
  { key: "3y",  label: "3Y",  months: 36  },
];

function fmtMonthYear(iso: string): string {
  const d = new Date(iso + "T00:00:00Z");
  return d.toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

function fmtShortDate(iso: string): string {
  const d = new Date(iso + "T00:00:00Z");
  const month = d.toLocaleDateString("en-US", { month: "short", timeZone: "UTC" });
  const yy = String(d.getUTCFullYear()).slice(2);
  return `${month} '${yy}`;
}

function fmtBillions(usdMillions: number, decimals = 1): string {
  return `$${(usdMillions / 1000).toFixed(decimals)}B`;
}

function fmtAxisBillions(usdMillions: number): string {
  const b = usdMillions / 1000;
  return b >= 10 ? `$${b.toFixed(0)}B` : `$${b.toFixed(1)}B`;
}

function fmtPctSigned(v: number | null): string {
  if (v == null || !isFinite(v)) return "—";
  return `${v >= 0 ? "+" : ""}${v.toFixed(1)}%`;
}

function fmtAxisPct(v: number): string {
  return `${v > 0 ? "+" : ""}${Math.round(v)}%`;
}

// Build the working slice for the chosen range. We render the same point
// list in both the price chart and the YoY chart so the X axes line up
// pixel-for-pixel.
type YoyPoint = ChartPoint & { yoy: number | null };

export default function DataCentersChart({ data }: { data: ChartPoint[] }) {
  const [range, setRange] = useState<RangeKey>("all");
  const [showGeneral, setShowGeneral] = useState(true);

  // YoY needs 12 prior months of context. We always compute YoY on the
  // FULL series first, then slice to the visible range — that way the
  // first month of every range still has a valid YoY value (otherwise
  // every range would start with 12 blank months).
  const enriched = useMemo<YoyPoint[]>(() => {
    const out: YoyPoint[] = data.map((p) => ({ ...p, yoy: null }));
    for (let i = 12; i < out.length; i++) {
      const cur = out[i].dataCenter;
      const prev = out[i - 12].dataCenter;
      if (cur != null && prev != null && prev !== 0) {
        out[i].yoy = ((cur - prev) / prev) * 100;
      }
    }
    return out;
  }, [data]);

  const filtered = useMemo(() => {
    const r = RANGES.find((x) => x.key === range)!;
    if (r.months == null) return enriched;
    return enriched.slice(-r.months);
  }, [enriched, range]);

  // Y-domain for the price chart: pad upper bound a touch so the line
  // never hugs the top edge of the plotting area.
  const maxValue = useMemo(() => {
    let max = 0;
    for (const p of filtered) {
      if (p.dataCenter != null && p.dataCenter > max) max = p.dataCenter;
      if (showGeneral && p.general != null && p.general > max) max = p.general;
    }
    return max * 1.08;
  }, [filtered, showGeneral]);

  // Y-domain for the YoY chart: symmetric-ish around 0, padded a touch.
  const yoyDomain = useMemo<[number, number]>(() => {
    let min = 0;
    let max = 0;
    for (const p of filtered) {
      if (p.yoy != null) {
        if (p.yoy < min) min = p.yoy;
        if (p.yoy > max) max = p.yoy;
      }
    }
    const pad = Math.max(5, Math.round(Math.max(Math.abs(min), Math.abs(max)) * 0.12));
    return [Math.min(min - pad, -5), max + pad];
  }, [filtered]);

  // Tick selection — let Recharts decide most of it, but force ~6 ticks
  // on the X axis so the labels don't crowd
  const xInterval = Math.max(1, Math.floor(filtered.length / 6));

  return (
    <div style={{ fontFamily: FONT_BASTION }}>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Legend
            color={DATA_CENTER_COLOR}
            label="Data center"
            active
            onClick={() => {}}
          />
          <Legend
            color={GENERAL_OFFICE_COLOR}
            label="General office"
            active={showGeneral}
            onClick={() => setShowGeneral((v) => !v)}
          />
        </div>
        <div className="flex items-center gap-1">
          {RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              onClick={() => setRange(r.key)}
              className="transition-colors"
              style={{
                fontSize: 11,
                fontWeight: 600,
                padding: "5px 10px",
                borderRadius: 6,
                background:
                  range === r.key
                    ? "rgba(125,183,255,0.18)"
                    : "rgba(255,255,255,0.04)",
                border:
                  range === r.key
                    ? "1px solid rgba(125,183,255,0.40)"
                    : "1px solid rgba(255,255,255,0.06)",
                color: range === r.key ? "#7DB7FF" : "#b0b0b0",
                cursor: "pointer",
                letterSpacing: "0.02em",
              }}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main chart — absolute CapEx */}
      <div style={{ width: "100%", height: 300 }}>
        <ResponsiveContainer>
          <ComposedChart
            data={filtered}
            margin={{ top: 6, right: 16, bottom: 0, left: 0 }}
          >
            <defs>
              <linearGradient id="dcGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={DATA_CENTER_COLOR} stopOpacity={0.34} />
                <stop offset="100%" stopColor={DATA_CENTER_COLOR} stopOpacity={0.02} />
              </linearGradient>
            </defs>

            <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />

            <XAxis
              dataKey="date"
              tickFormatter={fmtShortDate}
              interval={xInterval}
              tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 11 }}
              tickLine={false}
              axisLine={{ stroke: "rgba(255,255,255,0.08)" }}
              minTickGap={20}
            />

            <YAxis
              tickFormatter={fmtAxisBillions}
              tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              width={56}
              domain={[0, maxValue]}
            />

            <Tooltip
              cursor={{ stroke: "rgba(255,255,255,0.15)", strokeWidth: 1 }}
              content={<CustomTooltip showGeneral={showGeneral} />}
            />

            {showGeneral && (
              <Line
                type="monotone"
                dataKey="general"
                stroke={GENERAL_OFFICE_COLOR}
                strokeWidth={1.7}
                dot={false}
                strokeDasharray="3 3"
                isAnimationActive={false}
              />
            )}

            <Area
              type="monotone"
              dataKey="dataCenter"
              stroke={DATA_CENTER_COLOR}
              strokeWidth={2}
              fill="url(#dcGradient)"
              dot={false}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* YoY sub-chart — same X axis, % change vs same month one year ago */}
      <div style={{ marginTop: 14 }}>
        <div
          className="flex items-center gap-2 mb-1"
          style={{ paddingLeft: 4 }}
        >
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.45)",
            }}
          >
            YoY change
          </span>
          <span style={{ fontSize: 10, color: "rgba(255,255,255,0.30)" }}>
            vs same month, one year ago
          </span>
        </div>
        <div style={{ width: "100%", height: 110 }}>
          <ResponsiveContainer>
            <ComposedChart
              data={filtered}
              margin={{ top: 2, right: 16, bottom: 0, left: 0 }}
            >
              <CartesianGrid stroke="rgba(255,255,255,0.04)" vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={fmtShortDate}
                interval={xInterval}
                tick={{ fill: "rgba(255,255,255,0.45)", fontSize: 10 }}
                tickLine={false}
                axisLine={{ stroke: "rgba(255,255,255,0.08)" }}
                minTickGap={20}
              />
              <YAxis
                tickFormatter={fmtAxisPct}
                tick={{ fill: "rgba(255,255,255,0.45)", fontSize: 10 }}
                tickLine={false}
                axisLine={false}
                width={56}
                domain={yoyDomain}
              />
              <ReferenceLine y={0} stroke="rgba(255,255,255,0.18)" />
              <Tooltip
                cursor={{ stroke: "rgba(255,255,255,0.15)", strokeWidth: 1 }}
                content={<YoyTooltip />}
              />
              <Bar
                dataKey="yoy"
                isAnimationActive={false}
                radius={[1, 1, 0, 0]}
              >
                {filtered.map((point) => (
                  <Cell
                    key={point.date}
                    fill={
                      point.yoy == null
                        ? "rgba(255,255,255,0.18)"
                        : point.yoy >= 0
                          ? POSITIVE_COLOR
                          : NEGATIVE_COLOR
                    }
                  />
                ))}
              </Bar>
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function Legend({
  color,
  label,
  active,
  onClick,
}: {
  color: string;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 transition-opacity"
      style={{
        opacity: active ? 1 : 0.4,
        background: "rgba(255,255,255,0.025)",
        border: "1px solid rgba(255,255,255,0.06)",
        borderRadius: 6,
        padding: "4px 8px",
        cursor: "pointer",
      }}
    >
      <span
        style={{
          display: "inline-block",
          width: 10,
          height: 10,
          borderRadius: 2,
          background: color,
        }}
      />
      <span style={{ fontSize: 11, color: "#cfcfcf", fontWeight: 500 }}>
        {label}
      </span>
    </button>
  );
}

function CustomTooltip({
  active,
  payload,
  label,
  showGeneral,
}: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; dataKey: string; payload: YoyPoint }>;
  label?: string;
  showGeneral: boolean;
}) {
  if (!active || !payload || payload.length === 0 || !label) return null;
  const p = payload[0].payload;
  const lines: Array<{ color: string; label: string; value: number | null; isPct?: boolean }> = [
    { color: DATA_CENTER_COLOR, label: "Data center", value: p.dataCenter },
  ];
  if (showGeneral) {
    lines.push({
      color: GENERAL_OFFICE_COLOR,
      label: "General office",
      value: p.general,
    });
  }
  lines.push({ color: "rgba(255,255,255,0.4)", label: "YoY", value: p.yoy, isPct: true });

  return (
    <div
      style={{
        background: "#1a1a1a",
        border: "1px solid rgba(255,255,255,0.12)",
        borderRadius: 8,
        padding: "8px 12px",
        fontFamily: FONT_BASTION,
        boxShadow: "0 4px 14px rgba(0,0,0,0.45)",
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: "rgba(255,255,255,0.55)",
          marginBottom: 6,
          letterSpacing: "0.02em",
          textTransform: "uppercase",
        }}
      >
        {fmtMonthYear(label)}
      </div>
      {lines.map((ln) => (
        <div
          key={ln.label}
          className="flex items-center justify-between gap-4"
          style={{ minWidth: 200, padding: "2px 0" }}
        >
          <span className="inline-flex items-center gap-2">
            <span
              style={{
                display: "inline-block",
                width: 8,
                height: 8,
                borderRadius: 2,
                background: ln.color,
              }}
            />
            <span style={{ fontSize: 12, color: "#cfcfcf" }}>{ln.label}</span>
          </span>
          <span
            style={{
              fontFamily: FONT_MONO,
              fontSize: 13,
              fontWeight: 700,
              color:
                ln.isPct && ln.value != null
                  ? ln.value >= 0
                    ? "#3bd576"
                    : "#ef4444"
                  : "#ffffff",
            }}
          >
            {ln.value == null
              ? "—"
              : ln.isPct
              ? fmtPctSigned(ln.value)
              : fmtBillions(ln.value, 1)}
          </span>
        </div>
      ))}
    </div>
  );
}

function YoyTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ value: number; payload: YoyPoint }>;
  label?: string;
}) {
  if (!active || !payload || payload.length === 0 || !label) return null;
  const v = payload[0].payload.yoy;
  return (
    <div
      style={{
        background: "#1a1a1a",
        border: "1px solid rgba(255,255,255,0.12)",
        borderRadius: 8,
        padding: "6px 10px",
        fontFamily: FONT_BASTION,
        boxShadow: "0 4px 14px rgba(0,0,0,0.45)",
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 600,
          color: "rgba(255,255,255,0.55)",
          marginBottom: 4,
          letterSpacing: "0.02em",
          textTransform: "uppercase",
        }}
      >
        {fmtMonthYear(label)} · YoY
      </div>
      <div
        style={{
          fontFamily: FONT_MONO,
          fontSize: 14,
          fontWeight: 700,
          color: v == null ? "#999" : v >= 0 ? "#3bd576" : "#ef4444",
        }}
      >
        {fmtPctSigned(v)}
      </div>
    </div>
  );
}
